/**
 * Ťahanie prstom po obrazovke – „chyť a posuň", ako na telefóne.
 *
 * ── Prečo to vôbec treba ──────────────────────────────────────────────────
 * Displej pri baliacom stole (LG 17BR30T-B) je **jednodotykový**. Windows
 * takýto panel nehlási ako dotykové zariadenie, ale ako **myš s absolútnou
 * polohou** – prst je pre systém kurzor, ktorý si niekam skočí a stlačí
 * tlačidlo. Prehliadač preto nedostane dotykové udalosti a nemá čo posúvať:
 * ťahanie po obrazovke je pre neho ťahanie myšou, teda označovanie textu.
 * Fungovať zostane iba posuvník na kraji, ktorý sa na dotyk trafí ťažko.
 *
 * **Aplikácia to nerieši.** Čokoľvek postavené na prehliadači (PWA, Electron,
 * WebView) dostane presne tie isté udalosti, lebo problém je o poschodie
 * nižšie – v tom, ako panel hlási sám seba operačnému systému. Preto si
 * posúvanie spravíme sami: na tom, či je to „myš" alebo „prst", nám potom
 * nezáleží.
 *
 * ── Ako to funguje ────────────────────────────────────────────────────────
 * Počúva sa `pointerdown` na celom dokumente. Kým sa prst nepohne o pár
 * bodov, nedeje sa nič – ťuknutie musí ostať ťuknutím, inak by sa nedalo
 * stlačiť žiadne tlačidlo. Až keď prekročí prah, začne sa posúvať a klik na
 * konci sa zahodí: kto ťahal, nechcel klikať.
 *
 * Po pustení sa pohyb ešte chvíľu dobehne (zotrvačnosť), pretože bez toho
 * pôsobí posúvanie mŕtvo a dlhý zoznam sa prechádza po centimetroch.
 *
 * ── Čoho sa to nedotýka ───────────────────────────────────────────────────
 * Polí, do ktorých sa píše, prepínačov a posuvníkov – tam by ťahanie bralo
 * to, čo prvok robí sám. A prvkov s `data-bez-tahania`, keby sa niekde
 * ukázalo niečo, čo si ťahanie rieši po svojom.
 */

/** Prah v bodoch, po ktorom je z ťuknutia ťahanie. */
const PRAH = 8;
/** Ako rýchlo dobieha zotrvačnosť (menšie číslo = kratší dojazd). */
const TLM = 0.94;
/** Pod touto rýchlosťou (bodov/ms) sa už nedobieha. */
const STOP = 0.02;

const nepatri = (el) => !!el?.closest?.(
  'input, textarea, select, option, [contenteditable="true"], [data-bez-tahania]');

/**
 * Najbližší predok, ktorý sa naozaj dá posúvať.
 *
 * Hľadá sa preto, že obrazovka nie je vždy jedna dlhá stránka – zoznam
 * v okne alebo bočné menu majú vlastné posúvanie a ťahanie v nich má hýbať
 * nimi, nie stránkou pod nimi.
 */
function posuvatelny(el) {
  let x = el;
  while (x && x !== document.body && x !== document.documentElement) {
    if (x.scrollHeight - x.clientHeight > 2) {
      const st = getComputedStyle(x).overflowY;
      if (st === 'auto' || st === 'scroll') return x;
    }
    x = x.parentElement;
  }
  return null;
}

let odpojit = null;

/** Zapne ťahanie prstom. Opakované volanie nič nepokazí. */
export function zapniTahanie() {
  if (odpojit) return;

  let aktivny = false;      // beží gesto?
  let tahame = false;       // prekročil sa prah?
  /* Klik po ťahaní sa zahadzuje – ale len ten jeden, ktorý po ňom naozaj
   * príde. Keby sa príznak nechal na to, že ho zhodí až samotný klik,
   * a prehliadač by po ťahaní žiadny neposlal, zjedol by sa **ďalší**
   * a ďalšie ťuknutie by neotvorilo nič. Preto sa nuluje pri každom
   * novom dotyku, nie až pri kliku. */
  let zahodKlik = false;
  let zac = { x: 0, y: 0 };
  let posledne = { y: 0, t: 0 };
  let rychlost = 0;
  let ciel = null;          // čo posúvame (prvok alebo null = okno)
  let dojazd = 0;

  const posun = () => (ciel ? ciel.scrollTop : window.scrollY);
  const posunNa = (v) => {
    if (ciel) ciel.scrollTop = v;
    else window.scrollTo(0, v);
  };

  const dole = (e) => {
    /* Len hlavné tlačidlo. Pravý klik ani druhý prst nemajú čo posúvať. */
    if (!e.isPrimary || (e.pointerType === 'mouse' && e.button !== 0)) return;
    if (nepatri(e.target)) return;
    cancelAnimationFrame(dojazd);
    aktivny = true;
    tahame = false;
    zahodKlik = false;
    ciel = posuvatelny(e.target);
    zac = { x: e.clientX, y: e.clientY };
    posledne = { y: e.clientY, t: e.timeStamp };
    rychlost = 0;
  };

  const pohyb = (e) => {
    if (!aktivny) return;
    const dy = e.clientY - zac.y;
    if (!tahame) {
      /* Vodorovný ťah necháme tak: býva to posúvanie niečoho vnútri, alebo
       * len nepresné ťuknutie. Rozhoduje prevaha zvislého smeru. */
      if (Math.abs(dy) < PRAH || Math.abs(dy) <= Math.abs(e.clientX - zac.x)) return;
      tahame = true;
      /* Označovanie textu počas ťahania vyzerá ako porucha – celý zoznam
       * zmodrie. Vypne sa na čas gesta a hneď sa vráti. */
      document.body.style.userSelect = 'none';
    }
    const dt = Math.max(1, e.timeStamp - posledne.t);
    rychlost = (e.clientY - posledne.y) / dt;
    posunNa(posun() - (e.clientY - posledne.y));
    posledne = { y: e.clientY, t: e.timeStamp };
    e.preventDefault();
  };

  const hore = () => {
    if (!aktivny) return;
    aktivny = false;
    document.body.style.userSelect = '';
    if (!tahame) return;
    zahodKlik = true;

    /* Dojazd. Bez neho sa dlhý zoznam prechádza po centimetroch a obsluha
     * ťahá desaťkrát namiesto raz. */
    let v = rychlost;
    const krok = () => {
      v *= TLM;
      if (Math.abs(v) < STOP) return;
      posunNa(posun() - v * 16);
      dojazd = requestAnimationFrame(krok);
    };
    if (Math.abs(v) > STOP) dojazd = requestAnimationFrame(krok);
  };

  /* Klik po ťahaní sa zahodí. Bez toho by sa pri posúvaní zoznamu otvorila
   * objednávka, nad ktorou prst náhodou skončil – a to je pri baliacom stole
   * to najotravnejšie, čo sa môže stať. */
  const klik = (e) => {
    if (!zahodKlik) return;
    zahodKlik = false;
    e.stopPropagation();
    e.preventDefault();
  };

  document.addEventListener('pointerdown', dole, { passive: true });
  document.addEventListener('pointermove', pohyb, { passive: false });
  document.addEventListener('pointerup', hore, { passive: true });
  document.addEventListener('pointercancel', hore, { passive: true });
  document.addEventListener('click', klik, true);

  odpojit = () => {
    cancelAnimationFrame(dojazd);
    document.body.style.userSelect = '';
    document.removeEventListener('pointerdown', dole);
    document.removeEventListener('pointermove', pohyb);
    document.removeEventListener('pointerup', hore);
    document.removeEventListener('pointercancel', hore);
    document.removeEventListener('click', klik, true);
    odpojit = null;
  };
}

/** Vypne ťahanie a vráti stránku do pôvodného správania. */
export function vypniTahanie() {
  odpojit?.();
}

export const jeZapnute = () => !!odpojit;
